import { useSelect } from '@refinedev/antd';
import { Select, type SelectProps } from 'antd';
import type { CrudFilter, BaseKey } from '@refinedev/core';
import { FacilityType, type IFacility } from '@admin-panel/types';

export const ALL_FACILITIES_VALUE = 'ALL_FACILITIES';

export type FacilitySelectValue = BaseKey | Array<BaseKey> | undefined;

export type FacilitySelectProps = Omit<SelectProps<FacilitySelectValue>, 'options'> & {
  filters?: Array<CrudFilter>;
  showAllFacilitiesOption?: boolean;
  onAllFacilitiesSelected?: (facilityIds: Array<number>) => void;
  setFacility?: (facility: IFacility | undefined) => void;
  selectedFacility?: IFacility;
  /** Restrict options to these facility types. When omitted, no type filtering is applied. */
  facilityTypes?: Array<FacilityType>;
  /** Drop these facility ids from the options. */
  excludeIds?: Array<number>;
  /** Restrict options to facilities that are enabled for GRN. */
  grnEnabledOnly?: boolean;
  /** Include disabled facilities when selecting a historical source facility. */
  enabledOnly?: boolean;
};

export const FacilitySelect = ({
  filters: additionalFilters,
  showAllFacilitiesOption,
  onAllFacilitiesSelected,
  setFacility,
  onChange,
  onSelect,
  onDeselect,
  value,
  defaultValue,
  allowClear = true,
  showSearch = true,
  placeholder = 'Choose Facility',
  selectedFacility: selectedFacilityProp,
  facilityTypes,
  excludeIds,
  grnEnabledOnly,
  enabledOnly = true,
  ...restSelectProps
}: FacilitySelectProps) => {
  const filters: Array<CrudFilter> = [
    ...(enabledOnly ? [{ field: 'enabled', value: 'true', operator: 'eq' } as CrudFilter] : []),
    ...(additionalFilters ?? []),
  ];

  const normalizePrimitive = (raw: BaseKey) => {
    if (raw === ALL_FACILITIES_VALUE) {
      return raw;
    }

    const asNumber = Number(raw);
    return Number.isNaN(asNumber) ? raw : asNumber;
  };

  const normalizeValue = (rawValue: FacilitySelectValue | null): FacilitySelectValue => {
    if (rawValue === ALL_FACILITIES_VALUE) {
      return rawValue;
    }

    if (rawValue === undefined || rawValue === null || rawValue === '') {
      return undefined;
    }

    if (Array.isArray(rawValue)) {
      const normalized = rawValue
        .map(value => {
          if (value === ALL_FACILITIES_VALUE) {
            return value;
          }

          if (value === undefined || value === null || value === '') {
            return undefined;
          }

          return normalizePrimitive(value);
        })
        .filter((value): value is BaseKey => value !== undefined);

      return normalized.length ? normalized : undefined;
    }

    return normalizePrimitive(rawValue);
  };

  const normalizedValue = normalizeValue(value);
  const normalizedDefaultValue = normalizeValue(defaultValue ?? value);

  const { selectProps, query } = useSelect<IFacility>({
    resource: 'facilities',
    defaultValue:
      normalizedDefaultValue === ALL_FACILITIES_VALUE ? undefined : normalizedDefaultValue,
    pagination: {
      pageSize: 1000,
    },
    filters,
    optionLabel: 'name',
    optionValue: 'id',
    debounce: 500,
    onSearch: searchValue => [
      {
        field: 'name',
        operator: 'startswith',
        value: searchValue,
      },
    ],
  });

  const facilityOptions = query.data?.data as Array<IFacility> | undefined;

  const handleChange: SelectProps<FacilitySelectValue>['onChange'] = (nextValue, option) => {
    if (nextValue === undefined) {
      onChange?.(nextValue, option);
      return;
    }

    if (nextValue === ALL_FACILITIES_VALUE) {
      const allFacilityIds = facilityOptions?.map(facility => facility.id) ?? [];
      onChange?.(nextValue, option);
      onAllFacilitiesSelected?.(allFacilityIds);
      return;
    }

    if (Array.isArray(nextValue) && nextValue.length === 0) {
      onChange?.(nextValue, option);
      return;
    }

    const currentSelectedValue = Array.isArray(nextValue)
      ? nextValue[nextValue.length - 1]
      : nextValue;

    if (currentSelectedValue !== undefined) {
      const selectedFacility = facilityOptions?.find(
        facility => facility.id === Number(currentSelectedValue),
      );

      if (selectedFacility) {
        setFacility?.(selectedFacility);
      }
    }

    onChange?.(nextValue, option);
  };

  const excludeIdSet = new Set((excludeIds ?? []).map(id => Number(id)));
  const typeSet =
    facilityTypes && facilityTypes.length > 0 ? new Set(facilityTypes) : undefined;

  const filteredBaseOptions = (selectProps.options ?? []).filter(option => {
    const optionId = Number(option.value);

    if (excludeIdSet.has(optionId)) {
      return false;
    }

    if (typeSet || grnEnabledOnly) {
      const facility = facilityOptions?.find(candidate => candidate.id === optionId);
      if (!facility) {
        return false;
      }
      if (typeSet && !typeSet.has(facility.facilityType)) {
        return false;
      }
      if (grnEnabledOnly && facility.grnEnabled !== true) {
        return false;
      }
    }

    return true;
  });

  const enhancedSelectProps: SelectProps<FacilitySelectValue> = {
    ...(selectProps as SelectProps<FacilitySelectValue>),
    options: showAllFacilitiesOption
      ? [{ label: 'All Facilities', value: ALL_FACILITIES_VALUE }, ...filteredBaseOptions]
      : filteredBaseOptions,
  };

  // Keep options as-is so vendor changes don't retain stale facilities.

  return (
    <Select
      {...enhancedSelectProps}
      {...restSelectProps}
      onChange={handleChange}
      onSelect={onSelect}
      onDeselect={onDeselect ?? onSelect}
      placeholder={placeholder}
      showSearch={showSearch}
      allowClear={allowClear}
      value={normalizedValue}
      defaultValue={normalizedDefaultValue}
    />
  );
};
